/**
 * The player: the platform's own host, playing one PUBLISHED version for students and teachers —
 * guests, or inside a CET product that frames it (D75). The same page is served on the studio's
 * domain at `/player/` for admins, where it plays any version, drafts included (D76).
 *
 * Its own origin, its own domain, no authoring chrome. What it is not is a second set of rules: the conversation with the
 * asset is `@cet/asset-host-session`, the same one the studio runs, so the two cannot disagree about
 * when marks are released or which writes a closed attempt refuses.
 *
 * Everything here is chrome: the header says what is playing and under which session, the footer
 * holds what only a host can do. The attempt lives in this page and nowhere else on this side
 * (invariant 3) — a guest's reload is a new learner; launched by Focus, Focus keeps it (D75).
 */

import { embed } from '@cet/asset-host';
import {
  HostSession,
  freeTalkOrigin,
  freeTalkPass,
  openExternal,
  clearAttempt,
  createSessionControls,
  newRecord,
} from '@cet/asset-host-session';
import type { ExternalHandle, FreeTalkPass } from '@cet/asset-host-session';
import { currentSession, initialsOf, signIn, signOut, ssoAvailable } from '@cet/asset-sso';
import { freeTalkOutcomeExtensions } from '@cet/asset-xapi';

import { createFooter } from './footer.js';
import { fetchRetrying503 } from './retry.js';
import { PROVIDERS, chooseProvider } from './providers/index.js';
import { AI_SERVICES, dirFor, isJudgement } from '@cet/asset-contract';
import type { AiPayload, FeedbackMode, ItemVerdict, Judgement, LocalizedText, Mode, PagePayload, SchemeDoc, Score, Tool } from '@cet/asset-contract';

/** The catalogue row, as much of it as a host reads. */
interface Row {
  readonly id: string;
  readonly version: string;
  readonly status: string;
  readonly title: LocalizedText;
  readonly locales: readonly string[];
  readonly hasScheme: boolean;
  /** The ids of the questions it grades — what an evaluation page knows without the scheme (D75). */
  readonly items?: readonly string[];
  /** Whether it is built from the widget kit (D62), read from its import map by the player's server. */
  readonly widgets?: boolean;
  /**
   * A widget-kit lesson's pages: the step each plays and the questions it asks, read by the server
   * the way the Focus export reads them (D77). Null for any other asset.
   */
  readonly steps?: readonly { readonly stepId: string; readonly items: readonly string[] }[] | null;
  /** The activity says it is an exam (its lesson's `focus`, or the tag `placement`), read by the server. */
  readonly exam?: boolean;
  /** The manifest's tags, as the server lists the version; `placement` marks a placement test. */
  readonly tags?: readonly string[];
  /** The language a language course teaches (D89), from its manifest — which way its pages read. */
  readonly teaches?: string | null;
}

/** The external activities open now, by the step that asked (D78). */
const externals = new Map<string, ExternalHandle>();
let freeTalk: FreeTalkPass | null = null;
/** The page that framed this player — Focus — for the bot kit's diagnostics. */
const platformOrigin = ((): string => { try { return document.referrer ? new URL(document.referrer).origin : location.origin; } catch { return location.origin; } })();

const MODES: readonly Mode[] = ['practice', 'exam', 'review', 'solution'];
const FEEDBACKS: readonly FeedbackMode[] = ['onAnswer', 'onCheck', 'onSubmit', 'none'];

const el = <T extends HTMLElement>(id: string): T => {
  const found = document.getElementById(id);
  if (found === null) throw new Error(`#${id} is missing from index.html`);
  return found as T;
};

const ui = {
  top: el<HTMLDivElement>('top'),
  title: el<HTMLHeadingElement>('title'),
  titleStatus: el<HTMLSpanElement>('title-status'),
  sessionSlot: el<HTMLDivElement>('session-slot'),
  localeSlot: el<HTMLDivElement>('locale-slot'),
  traceToggle: el<HTMLButtonElement>('trace-toggle'),
  traceCount: el<HTMLSpanElement>('trace-count'),
  fullscreen: el<HTMLButtonElement>('fullscreen'),
  stage: el<HTMLElement>('stage'),
  picker: el<HTMLElement>('picker'),
  pickerList: el<HTMLUListElement>('picker-list'),
  frame: el<HTMLDivElement>('frame'),
  problem: el<HTMLParagraphElement>('problem'),
  summary: el<HTMLElement>('summary'),
  summaryTitle: el<HTMLHeadingElement>('summary-title'),
  summaryBody: el<HTMLParagraphElement>('summary-body'),
  summaryAnswers: el<HTMLButtonElement>('summary-answers'),
  trace: el<HTMLElement>('trace'),
  traceClear: el<HTMLButtonElement>('trace-clear'),
  tabEvents: el<HTMLButtonElement>('tab-events'),
  tabXapi: el<HTMLButtonElement>('tab-xapi'),
  xapiPanel: el<HTMLDivElement>('xapi'),
  xapiLog: el<HTMLOListElement>('xapi-log'),
  xapiCount: el<HTMLSpanElement>('xapi-count'),
  xapiLrs: el<HTMLParagraphElement>('xapi-lrs'),
  log: el<HTMLOListElement>('log'),
  controls: el<HTMLElement>('controls'),
  account: el<HTMLButtonElement>('account'),
  accountInitials: el<HTMLSpanElement>('account-initials'),
  accountPop: el<HTMLDivElement>('account-pop'),
  accountAvatar: el<HTMLSpanElement>('account-avatar'),
  accountName: el<HTMLElement>('account-name'),
  accountRole: el<HTMLSpanElement>('account-role'),
  accountSession: el<HTMLButtonElement>('account-session'),
};

// ---------------------------------------------------------------- where things are

/**
 * Where published files are served from — the same two sources, in the same order, as the studio.
 *
 * Locally the asset origin is the registry's second listener on 3002, never this page's origin:
 * an asset must not share an origin with its host (D10), and this listener has no `/a/` route.
 */
const origins = {
  asset: ['localhost', '127.0.0.1'].includes(location.hostname)
    ? 'http://localhost:3002'
    : `${location.protocol}//${location.hostname}:3002`,
  published: '',
  /** The free-talk bot's address (D78), from the server. Empty: the mock bot. */
  freeTalkBot: '',
  /** Local work only: get the learner's pass from the bot's demo signer (FREE_TALK_DEMO_PASS). */
  freeTalkDemo: false,
  /** Whether the server asks the AI hub (D86). Without one, no recording leaves the browser. */
  aiHub: false,
};

async function loadOrigins(): Promise<void> {
  try {
    const response = await fetch('./dist/config.json', { cache: 'no-store' });
    if (!response.ok) return;
    const config = (await response.json()) as { assetOrigin?: string; publishedBase?: string; freeTalkBotUrl?: string; freeTalkDemoPass?: boolean; aiHub?: boolean };
    if (typeof config.assetOrigin === 'string') origins.asset = config.assetOrigin;
    if (typeof config.publishedBase === 'string') origins.published = config.publishedBase.replace(/\/$/, '');
    if (typeof config.freeTalkBotUrl === 'string') origins.freeTalkBot = config.freeTalkBotUrl;
    origins.freeTalkDemo = config.freeTalkDemoPass === true;
    origins.aiHub = config.aiHub === true;
  } catch {
    // The fallback above is what every localhost run wants.
  }
}

/**
 * Where a version's files are, by where its bytes live — the studio's rule, not whether it is live.
 *
 * The public player only ever gets a published row (D75); a draft or a version in review reaches
 * this page only on the studio's domain, which is admin-only (D76), and is served from the review
 * area. Unpublishing moves the pointer and deletes nothing, so an unpublished version is still in
 * the published area.
 */
const baseFor = (status: string): string =>
  status === 'draft' || status === 'review'
    ? `${origins.asset}/review`
    : origins.published !== ''
      ? origins.published
      : `${origins.asset}/a`;

/** What a non-published status is called on screen. Published says nothing; it is the normal case. */
const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft',
  review: 'In review',
  unpublished: 'Unpublished',
};

// ---------------------------------------------------------------- the provider

/**
 * How this product plays an activity — the footer, the xAPI vocabulary, who draws page controls.
 * Chosen once from the URL, because the asset is told at init whether the host draws navigation.
 */
const chosen = chooseProvider(new URLSearchParams(location.search));
const provider = chosen.provider;
/**
 * The launcher's learner, attempt and LRS, if this provider has a launcher — Focus's `slxapi` (D75).
 * Read here, before the page's first request, because reading it takes it off the address.
 */
const launch = provider.launch?.() ?? null;

// ---------------------------------------------------------------- state

/**
 * Whether the player draws a footer at all — which is whether it drives the flow (D66).
 *
 * A template-based asset (built from the widget kit) is content: the player drives its
 * pages and buttons, so it gets the footer in every view. A free asset owns its UI and its flow, so
 * the learner's view is the header and the asset: its own buttons move between its pages, and its
 * own finish is the submit. The tester's view (`?debug=1`) has the footer for both.
 */
const templateBased = (): boolean => player.row?.widgets === true;
const footerShown = (): boolean => player.debug || templateBased();

const record = newRecord();

const player = {
  row: null as Row | null,
  scheme: null as SchemeDoc | null,
  conformant: false,
  progress: 0,
  /** Where the asset says the learner is (D65); null until it says, and for a one-page asset. */
  page: null as PagePayload | null,
  /** The asset's tools for the footer (D98), as it last sent them. */
  tools: [] as readonly Tool[],
  done: false,
  scoring: false,
  problem: null as string | null,
  mountEpoch: 0,
  events: 0,
  /** `?hideHeader=1` — for a page that draws its own title and only wants the activity. */
  hideHeader: false,
  /** `?debug=1` — the tester's view: the events drawer, the session menu, and the footer's tools. */
  debug: false,
  /** The provider's evaluation (`mode=evaluation` or `exam`, D91): no Check, hint or mark, one "סיימתי". */
  evaluation: false,
  /** Whether the address named a mode; without one, the activity's own `exam` decides (D91). */
  modeGiven: false,
  /** The evaluation summary page is on screen, in place of the activity (it is the pager's last page). */
  onSummary: false,
  /**
   * `?page=N`: the page to open on, reached by the host's own `next` once the asset reports where
   * it starts. `target` is 0-based and null when there is nowhere to go; `from` is the page a
   * `next` was sent from, so a repeat report of it is waited out rather than answered twice.
   */
  jump: { target: null as number | null, from: null as number | null, timer: 0 },
  /** Until when the save a jump caused is still the jump's, not the learner's (see quietForJump). */
  jumpSettles: 0,
};

/** The provider's evaluation: an exam that shows no mark, ever (D91). */
function enterEvaluation(): void {
  player.evaluation = true;
  record.mode = 'exam';
  record.feedbackMode = 'none';
}

/** Mode, feedback and language: the same component the studio's toolbar uses. */
const session = createSessionControls({
  record,
  locales: () => player.row?.locales ?? [],
  enabled: () => player.conformant,
  onChange: ({ why, reinit }) => {
    trace(why);
    // Exam from the menu is the provider's evaluation too, as `mode=exam` in the address is.
    player.evaluation = false;
    if (provider.evaluation && record.mode === 'exam') enterEvaluation();
    writeUrl();
    if (reinit) void mount();
    else render();
  },
});
ui.sessionSlot.replaceWith(session.chip);

/** Debug only: which provider plays the activity. Chosen once at start, so a change reloads. */
const providerPick = Object.assign(document.createElement('div'), { className: 'sc-langs' });
providerPick.setAttribute('role', 'radiogroup');
providerPick.setAttribute('aria-label', 'Provider');
for (const id of Object.keys(PROVIDERS)) {
  const choice = Object.assign(document.createElement('button'), { type: 'button', textContent: id.charAt(0).toUpperCase() + id.slice(1) });
  choice.setAttribute('role', 'radio');
  choice.setAttribute('aria-checked', String(id === provider.id));
  choice.addEventListener('click', () => {
    if (id === provider.id) return;
    writeUrl();
    const params = new URLSearchParams(location.search);
    if (id === 'default') params.delete('provider');
    else params.set('provider', id);
    location.search = params.toString();
  });
  providerPick.append(choice);
}
session.chip.before(providerPick);
ui.localeSlot.replaceWith(session.locale);

/**
 * Whether this page holds the scheme (D75). In practice it does, and grades as the learner answers;
 * in exam it never does, and a submit is scored on the server against the server's copy.
 */
const pageHoldsScheme = (): boolean => record.mode !== 'exam';

/** Whether there is anything to grade, whether or not this page holds the rules. */
const gradable = (): boolean => player.row?.hasScheme === true;

/** The launcher's pseudonymous learner id — Focus's `slxapi` actor's account name — or null for a guest. */
function learnerRef(): string | null {
  const account = launch?.actor['account'];
  const name = typeof account === 'object' && account !== null ? (account as Record<string, unknown>)['name'] : undefined;
  return typeof name === 'string' && name !== '' ? name : null;
}

/** A recording as base64, for JSON. In chunks: one spread of a few MB overflows the call stack. */
function base64Of(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let at = 0; at < bytes.length; at += 0x8000) binary += String.fromCharCode(...bytes.subarray(at, at + 0x8000));
  return btoa(binary);
}

/** The actor's account home page — Focus's origin — or undefined for a guest. */
function learnerHomePage(): string | undefined {
  const account = launch?.actor['account'];
  const homePage = typeof account === 'object' && account !== null ? (account as Record<string, unknown>)['homePage'] : undefined;
  return typeof homePage === 'string' && homePage !== '' ? homePage : undefined;
}

/**
 * The page's Focus launch beside its learner, for an AI call (D111): the server posts a distress
 * flag to Focus with the launch's token, on the spot, and keeps nothing. Nothing identifying travels
 * beyond what the launch URL already carried. Empty for a guest, or a launch with no token.
 */
function launchForAi(): Record<string, unknown> {
  const ref = learnerRef();
  if (launch === null || launch.registration === null || launch.lrs.auth === null || ref === null) return {};
  const homePage = learnerHomePage();
  return { launch: launch.lrs, registration: launch.registration, learnerRef: ref, ...(homePage === undefined ? {} : { homePage }) };
}

/** An `ai` request, answered by this player's server (D86): its answer, or null on any failure. */
async function askServerAi(payload: AiPayload): Promise<unknown> {
  const row = player.row;
  if (row === null) return null;
  const transcribing = payload.service === 'transcribe';
  const input = transcribing ? { ...payload.input, audio: base64Of(payload.input['audio'] as ArrayBuffer) } : payload.input;
  try {
    const response = await fetch('/api/v1/ai', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // `launched`: a real launch never gets the mock, even one with no token to flag with (D106).
      body: JSON.stringify({ assetId: row.id, version: row.version, ...payload, input, launched: launch !== null, ...launchForAi() }),
      // Inside the SDK's 15 s; a recording's hub call is the longer one.
      signal: AbortSignal.timeout(transcribing ? 14_000 : 9_500),
    });
    if (!response.ok) return null;
    return ((await response.json()) as { value?: unknown }).value ?? null;
  } catch {
    return null;
  }
}

/** An open answer, judged by this player's server (D93): its signed judgement, or null on any failure. */
async function askServerJudge(itemId: string, text: string): Promise<Judgement | null> {
  const row = player.row;
  if (row === null) return null;
  const ref = learnerRef();
  try {
    const response = await fetch(`/assets/${encodeURIComponent(row.id)}/${encodeURIComponent(row.version)}/judge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ itemId, text, locale: record.locale, ...(ref === null ? {} : { learnerRef: ref }) }),
      // The hub's 8 s, and the hop to it.
      signal: AbortSignal.timeout(9_500),
    });
    if (!response.ok) return null;
    const judgement = ((await response.json()) as { judgement?: unknown }).judgement;
    return isJudgement(judgement) ? judgement : null;
  } catch {
    return null;
  }
}

/** A fresh learner pass for the bot, signed by this player's server for Focus's launch (D78); undefined for a guest. */
async function signFreeTalkPass(): Promise<string | undefined> {
  const row = player.row;
  const ref = learnerRef();
  if (launch === null || launch.registration === null || ref === null || row === null) return undefined;
  try {
    const response = await fetch('/api/v1/freetalk/pass', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ launch: launch.lrs, registration: launch.registration, learnerRef: ref, homePage: learnerHomePage(), assetId: row.id, track: launch.track }),
      // Inside the kit's 5 s wait for a renewal.
      signal: AbortSignal.timeout(4_000),
    });
    const answer = (await response.json()) as { ok?: boolean; assertion?: string; refused?: string; error?: string };
    if (answer.ok === true && typeof answer.assertion === 'string') return answer.assertion;
    console.warn(`[assets player] Free-Talk pass: ${answer.refused ?? answer.error ?? `HTTP ${response.status}`}`);
  } catch (error) {
    console.warn(`[assets player] Free-Talk pass: ${error instanceof Error ? error.message : String(error)}`);
  }
  return undefined;
}

/** The ids of the questions graded: from the scheme when held, else from the server's row. */
const itemIds = (): readonly string[] =>
  player.scheme !== null ? Object.keys(player.scheme.items) : (player.row?.items ?? []);

const host = new HostSession({
  record,
  scoreUrl: '/api/v1/score',
  scoreLabel: `${location.origin}/api/v1/score`,
  scheme: () => player.scheme,
  heldScoreUrl: () => {
    const row = player.row;
    if (row === null || pageHoldsScheme()) return null;
    return `/assets/${encodeURIComponent(row.id)}/${encodeURIComponent(row.version)}/score`;
  },
  locales: () => player.row?.locales ?? [],
  reloadScheme: loadScheme,
  // Whether this host draws the page controls is the provider's call; the asset is told at init.
  hostNavigation: () => provider.hostNavigation && footerShown(),
  // Whether the footer's Check is the page's, and whether it holds the asset's tools (D98).
  hostCheck: () => provider.hostCheck === true && footerShown(),
  hostTools: () => footerHasTools() && footerShown(),
  // An evaluation's submit is final: its questions and marks can be looked at, never answered again.
  canStartAgain: () => !player.evaluation,
  // After how many checks a question may show its answer: the provider's rule too (D66).
  solutionAfter: () => provider.solutionAfter ?? null,
  // After how many checks a question is closed: the provider's rule too (D97).
  maxTries: () => provider.maxTries ?? null,
  // Whether Check may be pressed before answering: the provider's rule too.
  checkUnanswered: () => provider.checkUnanswered === true,
  // What this provider opens for an asset, and where (D78).
  externalServices: () => provider.external?.services ?? [],
  // The AI, through this player's server: it holds the hub's key, the page never does (D86).
  ai: askServerAi,
  // Speech is written down only by the hub: with none, the browser's own words stand (D86).
  aiServices: () => (origins.aiHub ? AI_SERVICES : AI_SERVICES.filter((service) => service !== 'transcribe')),
  // An open answer is judged by this player's server, against its own copy of the question (D93).
  judge: askServerJudge,
  learnerRef,
  hooks: {
    external: (payload, reply) => {
      const frame = host.player?.iframe;
      const open = externals.get(payload.key);
      if (payload.action === 'layout') { open?.layout(payload.box ?? { x: 0, y: 0, width: 0, height: 0 }); return; }
      if (payload.action === 'close') { open?.close(); externals.delete(payload.key); reply('closed'); return; }
      if (frame === undefined || open !== undefined) { if (frame === undefined) reply('unavailable'); return; }
      // D99: the page the talk opened on — its `completed` carries the talk's outcome.
      const talkPage = player.page?.index;
      externals.set(payload.key, openExternal({
        frame,
        payload,
        placement: provider.external?.placement ?? 'panel',
        url: freeTalkOrigin(origins.freeTalkBot),
        pass: (freeTalk ??= freeTalkPass({ sign: signFreeTalkPass, botOrigin: freeTalkOrigin(origins.freeTalkBot), demo: origins.freeTalkDemo, note: trace })),
        platformOrigin,
        note: trace,
        closeLabel: record.locale === 'he' ? 'סגירה' : 'Close',
        onStatus: (status, finish) => {
          if (status !== 'open') externals.delete(payload.key);
          // D99: the bot's outcome is kept with the page that held the talk and rides its `completed`; the asset gets the status only.
          if (status === 'finished' && finish?.conversationOutcome && talkPage !== undefined) {
            xapi.outcomeOnPage.set(talkPage, finish.conversationOutcome);
          }
          trace(`external ${payload.service} "${payload.key}" → ${status}${freeTalkOrigin(origins.freeTalkBot) === '' ? ' (mock bot)' : ''}`);
          reply(status);
        },
      }));
    },
    note: (line) => {
      if (!quietForJump(line)) trace(line);
    },
    changed: () => render(),
    remount: (why) => {
      trace(why);
      writeUrl();
      void mount();
    },
    mount: () => mount(),
    scoring: (inFlight, problem) => {
      player.scoring = inFlight;
      player.problem = problem;
    },
    progress: (fraction) => {
      player.progress = fraction;
      reportWhere();
      render();
    },
    tools: (payload) => {
      player.tools = payload.tools;
      render();
    },
    page: (payload) => {
      player.page = payload;
      reportWhere();
      followPageParam(payload);
      writeUrl();
      render();
    },
    /**
     * The asset says the learner finished — and a free asset's finish IS the submit: it owns its
     * flow, and there is no footer of the player's to submit from. So the player scores it on the
     * server, closes the attempt and sends the final statements (completed, and the judgement).
     * The asset still only says it finished; it never scores (invariant 2).
     *
     * An asset with nothing to grade is simply complete. A closed attempt is not submitted twice.
     *
     * A template-based asset is different: the player owns its finish (Submit, or the provider's
     * finish button), so its "done" is completion only — and only where the provider counts it.
     */
    done: () => {
      player.done = true;
      if (templateBased()) {
        if (provider.xapi.completeOnDone && !xapi.completed) {
          sendXapi({ kind: 'completed' });
          xapi.completed = true;
        }
      } else if (gradable() && !host.readOnly() && !player.scoring) {
        trace('the asset finished · the player submits');
        void host.submit();
      } else if (!gradable() && !xapi.completed) {
        sendXapi({ kind: 'completed' });
        xapi.completed = true;
      }
      render();
    },
    answered: (report, verdict) => {
      // The page a question is answered on is the page it is on — what a page's completion counts.
      if (player.page !== null) xapi.pageOf.set(report.itemId, player.page.index);
      // Reported as it is given, or — focus — when it is checked, with its mark.
      if (provider.xapi.answeredAt === 'answer' || verdict !== null) {
        sendXapi({ kind: 'answered', ...report, verdict, ...onPage(report.itemId) });
      }
      render();
    },
    checked: (verdicts) => {
      if (provider.xapi.answeredAt === 'check') sendAnsweredWithMarks(verdicts);
      else sendXapi({ kind: 'checked', verdicts });
      render();
    },
    // One question cleared: the attempt goes on, and a page already completed stays so (D107). No
    // xAPI by default — a provider may report it when one needs to.
    itemCleared: () => render(),
    hinted: (payload) => {
      if (provider.xapi.hints) sendXapi({ kind: 'hinted', ...payload, ...onPage(payload.itemId) });
    },
    // The learner was shown a question's answer — Focus's "reveal answers", a help request too.
    solutionShown: (itemId) => {
      if (provider.xapi.hints) sendXapi({ kind: 'revealed', itemId, ...onPage(itemId) });
    },
    /**
     * The attempt is scored on the server and closed. In a focus evaluation that is "בדקו אותי": the
     * marks are shown, every question is locked, and the summary page opens — and the result goes to
     * Focus only at the second press, "סיימתי", as LO sends it. Everywhere else the result goes now.
     */
    submitted: (result) => {
      if (summaryOffered()) player.onSummary = true;
      if (twoStep()) {
        trace('checked · scored and locked · "סיימתי" sends the result');
        return;
      }
      sendFinal(result.total, result.perItem, result.parts);
    },
    // A cleared record is a new attempt, and xAPI says so with a new registration — unless Focus
    // named the attempt, which stays its own (see attemptRegistration).
    reset: () => {
      // A new attempt reports its own start; the old one's page is not news to it.
      endJump(false);
      xapi.registration = attemptRegistration();
      xapi.completed = false;
      forgetPages();
      player.onSummary = false;
    },
    error: (payload) => {
      if (payload.fatal) player.problem = `The activity failed: ${payload.message}`;
      render();
    },
    /**
     * A real host sizes the frame to what the asset reports, and this one does too — which is what
     * lets the PAGE scroll. A frame held at the window's height clipped every asset longer than the
     * window, and the content below the fold could not be reached at all.
     */
    resized: (height) => {
      const frame = host.player?.iframe;
      // A kit lesson scrolls inside its frame, which fills the room between the bars (see .frame.scrolls).
      if (frame !== undefined && !templateBased()) frame.style.height = `${Math.ceil(height)}px`;
    },
    fullscreen: () => enterFullscreen(),
  },
});

// ---------------------------------------------------------------- the events drawer

/**
 * Which way a line travelled, read off the sentence the session wrote.
 *
 * The session speaks in sentences because the studio's suite asserts on them; the direction is a
 * presentation of those sentences, not a second record, so it is derived here and nowhere else.
 */
function directionOf(line: string): { label: string; dir: 'in' | 'out' | 'host' } {
  if (/^(answer|saveState|hint|progress|page|complete|asset requested|FATAL|error)/.test(line)) {
    return { label: 'asset →', dir: 'in' };
  }
  if (/^(check → verdicts|check → the page|verdicts →|navigate →|tool →)/.test(line)) return { label: '→ asset', dir: 'out' };
  return { label: 'host', dir: 'host' };
}

function trace(line: string): void {
  const now = new Date();
  const pad = (value: number, width = 2): string => String(value).padStart(width, '0');
  const time = document.createElement('time');
  time.textContent =
    `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}.${pad(now.getMilliseconds(), 3)}`;
  const { label, dir } = directionOf(line);
  const direction = document.createElement('span');
  direction.className = 'dir';
  direction.dataset['dir'] = dir;
  direction.textContent = label;
  const text = document.createElement('span');
  text.className = 'text';
  text.textContent = line;
  const item = document.createElement('li');
  item.append(time, direction, text);
  ui.log.prepend(item);
  while (ui.log.childElementCount > 300) ui.log.lastElementChild?.remove();
  player.events += 1;
  ui.traceCount.textContent = String(player.events);
}

// ---------------------------------------------------------------- xAPI

/**
 * The statements a host would send for what this player observed — built on the player's server,
 * shown here, and forwarded: to the launcher's LRS when the provider has a launch (Focus's `slxapi`),
 * else to a test LRS when the server is configured with one.
 *
 * The page sends observed events, never statements: building them is server-side (D13, DESIGN's
 * reporting section). With a launch, the learner, the attempt and the activity's id are the
 * launcher's, and the page hands the server its endpoint and token to send with (D75). Otherwise the
 * learner is a fake test learner, because a guest has no real one. Nothing is stored anywhere
 * (invariant 3).
 */
const newRegistration = (): string =>
  typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) =>
        (Number(c) ^ (crypto.getRandomValues(new Uint8Array(1))[0]! & (15 >> (Number(c) / 4)))).toString(16),
      );

/**
 * The attempt's registration. The launcher's when it sent one, and kept across a reset: it keys the
 * attempt — and its saved state — on it, so a new one would orphan both. Otherwise a new one per attempt.
 */
const attemptRegistration = (): string => launch?.registration ?? newRegistration();

const xapi = {
  registration: attemptRegistration(),
  /** Whether this attempt already sent `completed` — xAPI says it once per attempt. */
  completed: false,
  /** The page and the progress last reported, since this mount — only a change is news. */
  sentPage: null as number | null,
  sentProgress: 0,
  /** Which page each question was answered on, this attempt. */
  pageOf: new Map<string, number>(),
  /** Pages already reported `completed` — once each; what Finished waits for (D107). */
  pagesCompleted: new Set<number>(),
  /** The latest outcome of a talk held on each page this attempt (D99) — rides that page's `completed`. */
  outcomeOnPage: new Map<number, Record<string, unknown>>(),
  count: 0,
  /** One request at a time, in order, so the list reads in the order things happened. */
  queue: Promise.resolve(),
};

const TEST_LEARNER = {
  objectType: 'Agent',
  name: 'Test learner',
  account: { homePage: location.origin, name: 'test-learner' },
} as const;

/** A page, as a statement's parent: 0-based, of how many. */
interface OnPage {
  readonly index: number;
  readonly count: number;
  readonly stepId?: string;
}

/**
 * The row's steps, one per page (D98) — while the asset pages the same way. A provider that keeps
 * D94's grouped pages counts fewer pages than steps, and then the row says nothing about them.
 */
const rowSteps = (): Row['steps'] =>
  player.page === null || player.row?.steps?.length === player.page.count ? player.row?.steps : null;

/** The lesson step a page plays — a widget-kit lesson's, by the row; otherwise none. */
const stepAt = (index: number): string | undefined => rowSteps()?.[index]?.stepId || undefined;

/**
 * The page a question is on: the lesson step that asks it, when the row says (a kit lesson) — else
 * the page it was answered on, which is all the protocol tells a host about any other asset.
 */
function pageIndexOf(itemId: string): number | undefined {
  const index = rowSteps()?.findIndex((step) => step.items.includes(itemId)) ?? -1;
  return index >= 0 ? index : xapi.pageOf.get(itemId);
}

/** The graded questions on a page: its step's, when known; else the ones answered on it. */
function questionsOn(index: number): string[] {
  const graded = new Set(itemIds());
  const step = rowSteps()?.[index];
  const on = step !== undefined ? [...step.items] : [...xapi.pageOf].filter(([, at]) => at === index).map(([itemId]) => itemId);
  return on.filter((itemId) => graded.has(itemId));
}

/**
 * The page a question is on, for its statement's parent, and the step that page plays — so a
 * question on a kit lesson is named by its step, as Focus's import names the item. Nothing for a
 * one-page activity, whose parent is the activity itself.
 */
function onPage(itemId: string): { page?: OnPage } {
  const shown = player.page;
  if (shown === null) return {};
  const index = pageIndexOf(itemId) ?? shown.index;
  const stepId = stepAt(index);
  return { page: { index, count: shown.count, ...(stepId === undefined ? {} : { stepId }) } };
}

type XapiEvent =
  | { kind: 'launched' }
  | { kind: 'page'; index: number; count: number; stepId?: string }
  | {
      kind: 'pageCompleted'; index: number; count: number; stepId?: string; verdicts: readonly ItemVerdict[];
      extensions?: Record<string, unknown>;
    }
  | { kind: 'answered'; itemId: string; response: unknown; verdict: unknown; page?: OnPage }
  | { kind: 'progressed'; fraction: number }
  | { kind: 'hinted'; itemId: string; hintId: string; index: number; page?: OnPage }
  | { kind: 'revealed'; itemId: string; page?: OnPage }
  | { kind: 'checked'; verdicts: unknown }
  | { kind: 'completed' }
  | { kind: 'submitted'; total: unknown; perItem: unknown; alreadyCompleted: boolean; results?: 'points'; parts?: unknown };

interface Statement {
  readonly timestamp: string;
  readonly verb: { readonly display: Record<string, string> };
  readonly object: { readonly id: string; readonly definition?: { readonly name?: Record<string, string> } };
  readonly result?: { readonly success?: boolean; readonly score?: { raw: number; max: number }; readonly response?: string };
}

function sendXapi(event: XapiEvent): void {
  const row = player.row;
  if (row === null) return;
  const context = {
    actor: launch?.actor ?? TEST_LEARNER,
    asset: { id: row.id, version: row.version, title: row.title },
    registration: xapi.registration,
    locale: record.locale,
  };
  // Where to send them and with what — the launcher's, never shown and never kept beyond this page.
  const lrsTarget = launch === null ? {} : { launch: launch.lrs };
  const lrsName = launch?.lrsName ?? 'the test LRS';
  xapi.queue = xapi.queue.then(async () => {
    try {
      const response = await fetch('/api/v1/xapi', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ profile: provider.xapi.profile, context, event, ...lrsTarget }),
      });
      if (!response.ok) {
        showLrs('failed', `The player's server refused the event (${response.status}).`);
        return;
      }
      const { statements, lrs } = (await response.json()) as {
        statements: Statement[];
        lrs: { status: number; ok: boolean; error?: string } | null;
      };
      for (const statement of statements) showStatement(statement);
      // Launched, the drawer is hidden — so what the launcher's LRS answered goes to the console too.
      if (launch !== null && lrs !== null) {
        const line = `[assets player] xAPI ${event.kind}: ${statements.length} statement(s) → ${launch.lrsName} ${lrs.status}`;
        if (lrs.ok) console.debug(line);
        else console.warn(`${line} - ${lrs.error ?? 'refused'}`);
      }
      if (lrs === null) showLrs('local', 'Shown only - no test LRS is configured on the server.');
      else if (lrs.ok) showLrs('sent', `Sent to ${lrsName} (${lrs.status}).`);
      else showLrs('failed', `${lrsName.replace(/^the/, 'The')} refused them (${lrs.status}) ${lrs.error ?? ''}`.trim());
    } catch (error) {
      showLrs('failed', `Could not reach the player's server: ${error instanceof Error ? error.message : String(error)}`);
    }
  });
}

/**
 * The attempt's result, to the LRS: in an evaluation, each answer first with its mark (nothing was
 * checked along the way), then the result itself. Once per attempt.
 */
function sendFinal(total: Score, perItem: readonly ItemVerdict[], parts?: Record<string, Score>): void {
  if (provider.xapi.answeredAt === 'check' && player.evaluation) sendAnsweredWithMarks(perItem);
  // A placement test reports its points share (Focus 084 places the learner by it).
  const placement = player.row?.tags?.map((tag) => tag.toLowerCase()).includes('placement') === true;
  sendXapi({
    kind: 'submitted', total, perItem, alreadyCompleted: xapi.completed,
    ...(placement ? { results: 'points' as const } : {}),
    // Each part's score beside the total (D93): what Focus places a learner by.
    ...(parts === undefined ? {} : { parts }),
  });
  xapi.completed = true;
}

/** One `answered` per marked question, with the response the host recorded for it. */
function sendAnsweredWithMarks(verdicts: readonly ItemVerdict[]): void {
  for (const verdict of verdicts) {
    const response = record.responses[verdict.itemId];
    if (response !== undefined) sendXapi({ kind: 'answered', itemId: verdict.itemId, response, verdict, ...onPage(verdict.itemId) });
  }
}

/**
 * A page `completed`, in Focus's format (provider `completePages`) — sent when, and only when, the
 * learner moves on from it with the player's navigation bar: Next on a page, and Finished on the
 * last, just before the attempt's own `completed`. Not for the asset's own navigation, not for a
 * `?page=` jump, and nothing in evaluation, which reports at its finish. Once per page per attempt.
 *
 * It carries the mean score of the page's questions that have a mark — given at a Check, or by a
 * submit — or no score for a page with none, a page of content. A kit lesson's page is named by its
 * step, and its questions are the step's (`questionsOn`).
 */
function completePage(): void {
  const page = player.page;
  if (provider.xapi.completePages !== true || player.evaluation || page === null) return;
  if (xapi.pagesCompleted.has(page.index)) return;
  xapi.pagesCompleted.add(page.index);
  const marks = questionsOn(page.index)
    .map((itemId) => record.released[itemId]?.verdict ?? record.verdicts?.find((verdict) => verdict.itemId === itemId))
    .filter((mark): mark is ItemVerdict => mark !== undefined);
  const stepId = stepAt(page.index);
  // D99: the outcome of a talk held on this page rides the page's own `completed`.
  const outcome = xapi.outcomeOnPage.get(page.index);
  const extensions = outcome === undefined ? {} : freeTalkOutcomeExtensions(outcome);
  sendXapi({
    kind: 'pageCompleted', index: page.index, count: page.count, ...(stepId === undefined ? {} : { stepId }), verdicts: marks,
    ...(Object.keys(extensions).length === 0 ? {} : { extensions: { ...extensions } }),
  });
}

function forgetPages(): void {
  xapi.pageOf.clear();
  xapi.pagesCompleted.clear();
  // A new attempt starts with no talk finished (D99).
  xapi.outcomeOnPage.clear();
}

function showLrs(state: 'local' | 'sent' | 'failed', message: string): void {
  ui.xapiLrs.dataset['state'] = state;
  ui.xapiLrs.textContent = message;
}

/** One row per statement: when, the verb, what it was about and the result — the JSON on open. */
function showStatement(statement: Statement): void {
  const time = document.createElement('span');
  time.className = 'when';
  time.textContent = statement.timestamp.slice(11, 23);
  const verb = document.createElement('span');
  verb.className = 'verb';
  verb.textContent = statement.verb.display['en-US'] ?? '';
  const name = statement.object.definition?.name;
  const target = (name === undefined ? undefined : name[record.locale] ?? Object.values(name)[0]) ?? statement.object.id;
  const result = statement.result;
  const outcome =
    result?.score !== undefined
      ? ` ${result.score.raw}/${result.score.max}`
      : result?.response !== undefined
        ? ` “${result.response}”`
        : '';
  const success = result?.success === undefined ? '' : result.success ? ' ✓' : ' ✗';
  const what = document.createElement('span');
  what.className = 'what';
  what.append(verb, ` ${target}`);
  if (outcome !== '' || success !== '') {
    const detail = document.createElement('span');
    detail.className = 'result';
    detail.textContent = `${outcome}${success}`;
    what.append(detail);
  }
  const summary = document.createElement('summary');
  summary.append(time, what);
  const json = document.createElement('pre');
  json.textContent = JSON.stringify(statement, null, 2);
  const details = document.createElement('details');
  details.append(summary, json);
  const item = document.createElement('li');
  item.append(details);
  ui.xapiLog.prepend(item);
  while (ui.xapiLog.childElementCount > 300) ui.xapiLog.lastElementChild?.remove();
  xapi.count += 1;
  ui.xapiCount.textContent = String(xapi.count);
}

function selectTab(which: 'events' | 'xapi'): void {
  ui.tabEvents.setAttribute('aria-selected', String(which === 'events'));
  ui.tabXapi.setAttribute('aria-selected', String(which === 'xapi'));
  ui.log.hidden = which !== 'events';
  ui.xapiPanel.hidden = which !== 'xapi';
}

ui.tabEvents.addEventListener('click', () => selectTab('events'));
ui.tabXapi.addEventListener('click', () => selectTab('xapi'));

// ---------------------------------------------------------------- the kept attempt

/**
 * The attempt the launcher keeps between visits (D70) — Focus's state API, when Focus launched the
 * player. Read before the asset is mounted, so it resumes as the learner left it, marks included;
 * saved as it changes; and a reset is simply the fresh record saved over the old one. This side
 * keeps nothing (invariant 3): a guest's attempt is this page's alone, and a reload starts afresh.
 */
const kept = {
  /** Saving begins only once the kept attempt was read, or found missing — so an empty record never
   *  overwrites one that could not be read. */
  ready: false,
  /** What was last saved, so an unchanged record is not sent again. */
  last: '',
  timer: 0,
};

/** The record's resumable part (D70). The mode, timing and language are the launch URL's, not kept. */
const KEPT = [
  'state',
  'responses',
  'judgements',
  'hintsUsed',
  'checks',
  'solutionsShown',
  'released',
  'verdicts',
  'verdictsLocale',
  'score',
  'serverScore',
  'serverParts',
  'browserScore',
  'submitted',
] as const;

/** Tied to the version it belongs to: another version's answers may not fit this one's questions. */
const keptDocument = (
  row: Row,
): { asset: string; record: Record<string, unknown>; pages: KeptPages; reported: boolean } => ({
  asset: `${row.id}@${row.version}`,
  record: Object.fromEntries(KEPT.map((field) => [field, record[field]])),
  // Which pages were reported, so a resumed attempt does not report them again.
  pages: { of: Object.fromEntries(xapi.pageOf), completed: [...xapi.pagesCompleted] },
  // Whether the result was sent — an evaluation checked but not yet finished has not.
  reported: xapi.completed,
});

interface KeptPages {
  readonly of: Record<string, number>;
  readonly completed: readonly number[];
}

const pageIndexes = (value: unknown): number[] =>
  Array.isArray(value) ? value.filter((index): index is number => Number.isInteger(index) && index >= 0) : [];

async function resume(row: Row): Promise<void> {
  kept.ready = false;
  kept.last = '';
  const store = launch?.state;
  if (store === undefined) return;
  try {
    const document = (await Promise.race([
      store.load(),
      new Promise((_, reject) => window.setTimeout(() => reject(new Error('no answer in 5s')), 5_000)),
    ])) as { asset?: unknown; record?: Record<string, unknown> } | null;
    if (document?.asset === `${row.id}@${row.version}` && typeof document.record === 'object' && document.record !== null) {
      const saved = document.record;
      const restored: Record<string, unknown> = {};
      for (const field of KEPT) {
        // Only a value of the shape the record holds there: a field this page cannot read stays fresh.
        const value = saved[field];
        const current: unknown = record[field];
        if (field in saved && (value === null || current === null || typeof value === typeof current)) restored[field] = value;
      }
      Object.assign(record, restored);
      const reported = (document as { reported?: unknown }).reported;
      xapi.completed = typeof reported === 'boolean' ? reported : record.submitted;
      // Checked and not yet finished: the learner comes back to the summary, as they left it.
      player.onSummary = summaryOffered() && record.submitted && !xapi.completed;
      const pages = (document as { pages?: Partial<Record<keyof KeptPages, unknown>> }).pages;
      forgetPages();
      if (typeof pages?.of === 'object' && pages.of !== null) {
        for (const [itemId, index] of Object.entries(pages.of)) {
          if (Number.isInteger(index) && (index as number) >= 0) xapi.pageOf.set(itemId, index as number);
        }
      }
      for (const index of pageIndexes(pages?.completed)) xapi.pagesCompleted.add(index);
      trace(`resumed the kept attempt${record.submitted ? ' · submitted' : ''} · ${Object.keys(record.responses).length} answer(s)`);
    } else if (document !== null && document !== undefined) {
      trace('the kept attempt is for another version · starting afresh');
    }
    kept.last = JSON.stringify(keptDocument(row));
    kept.ready = true;
  } catch (error) {
    trace(`could not read the kept attempt: ${error instanceof Error ? error.message : String(error)} · this visit is not kept`);
  }
}

/** Save the attempt once it settles — answers, marks and the asset's own state arrive in bursts. */
function keep(): void {
  if (!kept.ready) return;
  window.clearTimeout(kept.timer);
  kept.timer = window.setTimeout(() => void flushKept(), 800);
}

async function flushKept(keepalive = false): Promise<void> {
  window.clearTimeout(kept.timer);
  const store = launch?.state;
  const row = player.row;
  if (!kept.ready || store === undefined || row === null) return;
  const document = keptDocument(row);
  const json = JSON.stringify(document);
  if (json === kept.last) return;
  kept.last = json;
  if (!(await store.save(document, { keepalive }))) {
    // Tried again at the next change, with whatever the record is by then.
    kept.last = '';
    trace('the attempt could not be kept · trying again at the next change');
  }
}

// The page is going — Focus moving on, or the tab closing: the last change must not wait for a timer.
window.addEventListener('pagehide', () => void flushKept(true));

// ---------------------------------------------------------------- loading

async function loadScheme(): Promise<void> {
  player.scheme = null;
  const row = player.row;
  if (row === null || !row.hasScheme) return;
  if (!pageHoldsScheme()) {
    trace(`scheme for ${row.id}@${row.version} stays on the server - exam is scored there`);
    return;
  }
  const response = await fetchRetrying503(`/assets/${encodeURIComponent(row.id)}/${encodeURIComponent(row.version)}/scheme`);
  if (!response.ok) {
    trace(`no grading rules for ${row.id}@${row.version} (${response.status}) - answers are recorded, nothing is marked`);
    return;
  }
  player.scheme = (await response.json()) as SchemeDoc;
  trace(`scheme for ${row.id}@${row.version} handed over - practice grades in this page`);
}

async function mount(): Promise<void> {
  const epoch = (player.mountEpoch += 1);
  // An open external activity belongs to the frame going away.
  for (const open of externals.values()) open.close();
  externals.clear();
  host.player?.destroy();
  host.player = null;
  player.conformant = false;
  player.progress = 0;
  xapi.sentPage = null;
  xapi.sentProgress = 0;
  player.page = null;
  player.tools = [];
  player.done = false;
  ui.frame.replaceChildren();
  render();

  const row = player.row;
  if (row === null) return;

  // The scheme follows the mode: a change of mode in the session menu mounts again, through here.
  if (row.hasScheme && pageHoldsScheme() !== (player.scheme !== null)) {
    await loadScheme();
    if (epoch !== player.mountEpoch) return;
  }

  try {
    const mounted = await embed({
      container: ui.frame,
      src: `${baseFor(row.status)}/${row.id}/${row.version}/index.html`,
      title: pick(row.title) || row.id,
      ...host.callbacks(),
    });
    // A mount superseded while it waited for the handshake owns a live frame; drop it.
    if (epoch !== player.mountEpoch) {
      mounted.destroy();
      return;
    }
    host.player = mounted;
    player.conformant = true;
    trace(`mounted ${row.id}@${row.version} · ${record.mode} · ${host.feedback() ?? 'no timing'} · ${host.locale()}`);
    sendXapi({ kind: 'launched' });
  } catch (error) {
    if (epoch !== player.mountEpoch) return;
    const message = error instanceof Error ? error.message : String(error);
    trace(`mount refused: ${message}`);
    player.problem = `This asset did not start: ${message}`;
  }
  render();
}

async function choose(id: string, version: string): Promise<void> {
  kept.ready = false;
  player.row = null;
  player.scheme = null;
  player.problem = null;
  clearAttempt(record);
  record.state = null;
  xapi.registration = attemptRegistration();
  xapi.completed = false;
  forgetPages();
  player.onSummary = false;

  if (id !== '') {
    // A 503 is a store that could not say yet (AP-10): asked again, waiting what Retry-After says.
    const response = await fetchRetrying503(`/assets/${encodeURIComponent(id)}/${encodeURIComponent(version)}`);
    if (!response.ok) {
      const detail = ((await response.json().catch(() => ({}))) as { error?: string }).error;
      player.problem = detail ?? `${id}@${version} is not available (${response.status}).`;
      writeUrl();
      render();
      return;
    }
    player.row = (await response.json()) as Row;
    // No mode in the address: an activity that says it is an exam plays as one (D91).
    if (!player.modeGiven && player.row.exam === true) {
      if (provider.evaluation) enterEvaluation();
      else record.mode = 'exam';
    }
  }
  // Snapped to a language the asset declares before anything is mounted or written to the URL.
  record.locale = host.locale();
  writeUrl();
  // The launcher's kept attempt, before the asset is told what it has (D70).
  if (player.row !== null) await resume(player.row);
  await mount();
}

// ---------------------------------------------------------------- drawing

const pick = (text: LocalizedText): string => {
  const byLocale = text as Readonly<Record<string, string | undefined>>;
  return byLocale[record.locale] ?? byLocale['he'] ?? Object.values(byLocale)[0] ?? '';
};

const button = (label: string, className: string): HTMLButtonElement => {
  const element = document.createElement('button');
  element.type = 'button';
  element.className = className;
  element.textContent = label;
  return element;
};

/**
 * With no activity named, there is nothing to choose from: the player does not list the catalogue
 * (D75). A learner arrives by a link; the page says so.
 */
function renderPicker(): void {
  const empty = document.createElement('li');
  empty.className = 'picker-empty';
  empty.textContent = 'No activity is named. Open the player from the link you were given.';
  ui.pickerList.replaceChildren(empty);
}

function render(): void {
  const row = player.row;
  const live = player.conformant;
  const closed = host.readOnly();

  // Header
  ui.title.textContent = row === null ? 'Asset player' : pick(row.title) || row.id;
  document.title = ui.title.textContent;
  // A draft is not what a learner will see until it is published, and it expires — say so. Beside
  // the title rather than inside it, so a long title's ellipsis cannot cut it off. Only the admin
  // player (D76) is ever shown one.
  const status = row === null ? undefined : STATUS_LABEL[row.status];
  ui.titleStatus.hidden = status === undefined;
  ui.titleStatus.textContent = status ?? '';
  ui.titleStatus.dataset['status'] = row?.status ?? '';
  session.chip.hidden = row === null || !player.debug;
  providerPick.hidden = row === null || !player.debug;
  session.locale.hidden = row === null;
  session.render();

  // Stage
  ui.picker.hidden = row !== null || player.problem !== null;
  const summary =
    summaryOffered() && record.submitted
      ? provider.texts?.summary?.(record.serverScore?.scaled ?? 0, host.locale())
      : undefined;
  const onSummary = summary !== undefined && player.onSummary;
  ui.frame.hidden = row === null || onSummary;
  ui.frame.classList.toggle('scrolls', templateBased());
  ui.summary.hidden = !onSummary;
  if (summary !== undefined) {
    ui.summaryTitle.textContent = summary.title;
    ui.summaryBody.textContent = summary.body;
    ui.summaryAnswers.textContent = summary.myAnswers;
    ui.summary.dir = host.locale() === 'en' ? 'ltr' : 'rtl';
  }
  ui.problem.hidden = player.problem === null || player.scoring;
  ui.problem.textContent = player.problem ?? '';
  ui.controls.hidden = row === null || !footerShown();

  // Footer: what the host may do to this attempt, in this session, now
  const total = record.serverScore ?? (record.mode === 'review' ? record.score : null);
  const shown = total !== null && host.feedback() !== 'none';
  footer.render({
    live,
    replay: record.mode === 'review' || record.mode === 'solution',
    closed,
    submitted: record.submitted,
    scoring: player.scoring,
    gradable: gradable(),
    submitTitle: row !== null && !row.hasScheme ? 'This asset declares nothing to grade.' : '',
    // An evaluation shows no result at all, not even "withheld": the LRS alone gets it (D91).
    result: player.evaluation ? null : shown && total !== null ? { kind: 'score', total } : record.submitted ? { kind: 'withheld' } : null,
    page: footerPage(summary !== undefined),
    // An evaluation opens every page — to answer in, and afterwards to review.
    navigation: player.evaluation ? 'free' : provider.navigation,
    status: statusLine(),
    check: checkState(live, closed),
    tools: footerHasTools() ? player.tools : [],
    locale: host.locale(),
    // The pages read as the activity does: the language it teaches; a kit lesson, drawn in English
    // whatever the locale (D64); else the learner's.
    dir: row?.teaches ? dirFor(row.teaches) : row?.widgets === true ? 'ltr' : dirFor(host.locale()),
    finish: finishState(live),
  });
  // Whatever changed what is drawn may have changed the attempt; an unchanged one is not sent.
  keep();
}

/** The pager's pages: the activity's, and the summary as one more at the end once it exists. */
function footerPage(withSummary: boolean): PagePayload | null {
  if (!withSummary) return player.page;
  const page = player.page ?? { index: 0, count: 1 };
  return { index: player.onSummary ? page.count : page.index, count: page.count + 1 };
}

/** Focus evaluation's summary page: this provider has one, and this is its evaluation. */
const summaryOffered = (): boolean => player.evaluation && provider.texts?.summary !== undefined;

/**
 * The two presses — the check scores and locks, "סיימתי" sends the result — need the player's footer
 * to press them. A free asset finishes itself (its own finish is the submit), so its result goes at once.
 */
const twoStep = (): boolean => summaryOffered() && footerShown();

/** Where an evaluation stands: answering, checked (scored and locked), or finished (the result sent). */
const evalStage = (): 'answering' | 'checked' | 'finished' =>
  !record.submitted ? 'answering' : xapi.completed ? 'finished' : 'checked';

/**
 * "Scoring…" while a submit is out, and nothing otherwise. The result is the score badge's to say,
 * and an asset that only said it finished is the events drawer's to record.
 */
function statusLine(): string {
  return player.scoring ? 'Scoring…' : '';
}

/** Whether this provider's footer holds the asset's tools (D98). */
const footerHasTools = (): boolean =>
  [...provider.footer.start, ...provider.footer.middle, ...provider.footer.end].includes('tools');

/**
 * The footer's Check. The page's (D98): there only where marks are given in the moment — never in an
 * exam, a review or a closed attempt — and pressable when the asset says its question can be checked.
 * Otherwise the whole activity's, as it always was.
 */
function checkState(live: boolean, closed: boolean): { hidden: boolean; enabled: boolean } {
  if (provider.hostCheck !== true) return { hidden: closed, enabled: live };
  const feedback = host.feedback();
  const marks = !player.evaluation && (feedback === 'onCheck' || feedback === 'onAnswer');
  // A page with no question to check has no Check; nor has an asset that checks for itself.
  const none = player.page?.canCheck === undefined;
  return { hidden: closed || !marks || none, enabled: live && !player.scoring && player.page?.canCheck === true };
}

/**
 * The provider's finish button. Evaluation: "Check me", open from the start until the attempt is
 * submitted — an exam has no checks to wait for, and every page is open. Learning: "Finished", open
 * once every page is completed (D107): each one before the last left with Next, and the last done
 * by the lesson's own rule. A Reset afterwards does not take a completed page back.
 */
function finishState(live: boolean): { label: 'finish' | 'checkMe'; enabled: boolean } | null {
  if (provider.finish === undefined) return null;
  const open = live && !record.submitted && !player.scoring && gradable();
  if (player.evaluation) {
    const stage = evalStage();
    if (stage === 'answering') return { label: twoStep() ? 'checkMe' : 'finish', enabled: open };
    // Checked: "סיימתי" sends the result. Finished: nothing is left to press.
    return { label: 'finish', enabled: twoStep() && stage === 'checked' && live && !player.scoring };
  }
  // One bar (D98): "סיימתי" is the last page's, where Next has nowhere to go.
  const page = player.page;
  if (provider.hostCheck === true && page !== null && page.index < page.count - 1) return null;
  // Nothing to grade (a talk with the bot, D96): open once the lesson says it is done, until it completes.
  if (!gradable()) return { label: 'finish', enabled: live && player.done && !xapi.completed };
  const before = Array.from({ length: (page?.count ?? 1) - 1 }, (_, index) => index);
  return { label: 'finish', enabled: open && player.done && before.every((index) => xapi.pagesCompleted.has(index)) };
}

/**
 * Finishing. An evaluation is two presses: "בדקו אותי" warns about unanswered questions, then submits
 * — the server scores, the attempt closes, the summary opens — and "סיימתי" sends the result.
 * Learning is one: submit.
 */
async function finish(): Promise<void> {
  if (player.evaluation && evalStage() === 'checked') {
    if (record.serverScore !== null) sendFinal(record.serverScore, record.verdicts ?? [], record.serverParts ?? undefined);
    trace('finished · the result is sent');
    render();
    return;
  }
  if (player.evaluation && provider.texts !== undefined && gradable()) {
    const unanswered = itemIds().filter((itemId) => record.responses[itemId] === undefined);
    if (unanswered.length > 0) {
      const sure = await footer.confirm(provider.texts.unanswered(unanswered.length, host.locale()));
      if (!sure) {
        trace(`finish cancelled · ${unanswered.length} question(s) unanswered`);
        return;
      }
    }
  }
  // Finished is the last page's "continue": that page completes first, then the attempt — whose
  // submit closes it, and re-mounts the lesson read-only (`canWrite: false`).
  completePage();
  // Nothing to grade, nothing to submit: the attempt completes; the page's `completed` carried the talk's outcome (D99).
  if (!gradable()) {
    if (!xapi.completed) sendXapi({ kind: 'completed' });
    xapi.completed = true;
    trace('finished · completed is sent');
    render();
    return;
  }
  await host.submit();
}

/**
 * Submit closes the attempt, so it asks first when questions are unanswered — in every provider and
 * mode, as an evaluation's finish does (D94). Resolves false when the learner goes back to them.
 */
async function confirmUnanswered(): Promise<boolean> {
  if (!gradable()) return true;
  const count = itemIds().filter((itemId) => record.responses[itemId] === undefined).length;
  if (count === 0) return true;
  const he = host.locale() === 'he';
  const sure = await footer.confirm({
    message: he
      ? (count === 1 ? 'לא ענית על שאלה אחת. להגיש בכל זאת?' : `לא ענית על ${count} שאלות. להגיש בכל זאת?`)
      : (count === 1 ? 'One question is unanswered. Submit anyway?' : `${count} questions are unanswered. Submit anyway?`),
    ok: he ? 'להגיש' : 'Submit',
    cancel: he ? 'לחזור לשאלות' : 'Back to the questions',
  });
  if (!sure) trace(`submit cancelled · ${count} question(s) unanswered`);
  return sure;
}

async function submitAsked(): Promise<void> {
  if (await confirmUnanswered()) await host.submit();
}

// ---------------------------------------------------------------- the page in the URL

/**
 * Open on the page the URL names — a reload, or a link someone copied, comes back where it was.
 *
 * The protocol has no "go to page" (D65): the asset owns its pages, and a host only asks it to move
 * one on. So the player asks, one page at a time, as the asset reports each — the same `next` its
 * footer sends. It stops at the page named, at the last page, or where the asset does not move on;
 * the learner taking the controls ends it too. Where the learner was across sessions is the
 * asset's saved state (D70); this is only the address bar's part of it.
 */
function followPageParam(payload: PagePayload): void {
  const jump = player.jump;
  if (jump.target === null) return;
  // The page a `next` was just sent from, reported again — the asset re-reports when its rules change.
  if (jump.from !== null && payload.index === jump.from) return;
  window.clearTimeout(jump.timer);
  if (payload.index >= jump.target || payload.index >= payload.count - 1) {
    endJump();
    return;
  }
  jump.from = payload.index;
  host.navigate('next');
  // An asset that does not move on is not pushed again: the jump ends where it stopped.
  jump.timer = window.setTimeout(() => {
    trace(`page ${jump.target === null ? '' : jump.target + 1} not reached · the activity stayed on page ${payload.index + 1}`);
    endJump();
    writeUrl();
  }, 2000);
}

/** The jump is over: the page it ended on is where the learner is, and that is reported now. */
function endJump(report = true): void {
  const jumping = player.jump.target !== null;
  window.clearTimeout(player.jump.timer);
  player.jump = { target: null, from: null, timer: 0 };
  if (!report) return;
  reportWhere();
  // The landing, as one line where the jump's own traffic was: what the learner sees is this page.
  const page = player.page;
  if (jumping && page !== null) {
    trace(`page ${page.index + 1} of ${page.count}${page.canAdvance === false ? ' · forward locked' : ''}`);
    // The asset saves where it is a moment later (saves coalesce, D34); that save is the jump's too.
    player.jumpSettles = Date.now() + 2500;
  }
}

/**
 * Whether a line of the events drawer belongs to a `?page=` jump, and so is left out.
 *
 * Opening on page 4 from a link is two things to a person reading the drawer: the activity loaded,
 * and it is on page 4. The pages passed on the way, the Nexts that passed them, the progress and the
 * position saved as it went are the mechanism, not the session — so they are not drawn, and the
 * landing is drawn as one `page` line (endJump). The messages still happen and are still handled;
 * the record keeps the saved state. Only the drawer is quiet.
 */
function quietForJump(line: string): boolean {
  if (player.jump.target !== null) return /^(page |progress |saveState|navigate → )/.test(line);
  return Date.now() < player.jumpSettles && line.startsWith('saveState');
}

/**
 * The page the learner is on and how far through they are, as xAPI — only what changed.
 *
 * An asset reports the same page again when its rules for it change (`canAdvance`, D65), and the
 * same fraction after every keystroke; neither is news. And nothing is reported while a `?page=`
 * jump is on its way: the pages it passes were never in front of the learner, so a reload on page 3
 * is one "experienced Page 3", not pages 1, 2 and 3.
 */
function reportWhere(): void {
  if (player.jump.target !== null) return;
  const page = player.page;
  if (page !== null && page.index !== xapi.sentPage) {
    xapi.sentPage = page.index;
    const stepId = stepAt(page.index);
    sendXapi({ kind: 'page', index: page.index, count: page.count, ...(stepId === undefined ? {} : { stepId }) });
  }
  if (player.progress !== xapi.sentProgress) {
    xapi.sentProgress = player.progress;
    sendXapi({ kind: 'progressed', fraction: player.progress });
  }
}

// ---------------------------------------------------------------- the URL is the session's settings

/** `?asset=id@version&mode=&feedback=&locale=&page=` — so a link reproduces what someone was looking at. */
function readUrl(): { id: string; version: string } {
  const params = new URLSearchParams(location.search);
  const mode = params.get('mode');
  player.modeGiven = mode !== null;
  if (mode !== null && (MODES as readonly string[]).includes(mode)) record.mode = mode as Mode;
  const feedback = params.get('feedback');
  if (feedback !== null && (FEEDBACKS as readonly string[]).includes(feedback)) {
    record.feedbackMode = feedback as FeedbackMode;
  } else if (provider.defaultFeedback !== undefined) {
    record.feedbackMode = provider.defaultFeedback;
  }
  /**
   * Evaluation is this provider's exam: the learner is never shown a Check, hint or mark, not even
   * after the submit (exam + `none`); the result goes only to the LRS. Named as Focus's launcher sends it.
   */
  // `exam` is the same flow under this provider: one exam, whatever the address calls it.
  if ((mode === 'evaluation' || mode === 'exam') && provider.evaluation) enterEvaluation();
  const locale = params.get('locale');
  if (locale !== null) record.locale = locale;
  const hideHeader = params.get('hideHeader');
  player.hideHeader = hideHeader !== null && hideHeader !== '0' && hideHeader !== 'false';
  const debug = params.get('debug');
  player.debug = debug !== null && debug !== '0' && debug !== 'false';
  // 1-based on screen and in the URL; page 1 is where every activity opens anyway.
  const page = Number(params.get('page'));
  if (Number.isInteger(page) && page > 1) player.jump.target = page - 1;
  const [id = '', version = ''] = (params.get('asset') ?? '').split('@');
  return { id, version };
}

function writeUrl(): void {
  const params = new URLSearchParams();
  if (player.row !== null) params.set('asset', `${player.row.id}@${player.row.version}`);
  // Evaluation keeps its own name, as Focus's launcher writes it.
  params.set('mode', player.evaluation && record.mode === 'exam' ? 'evaluation' : record.mode);
  if (!(player.evaluation && record.mode === 'exam')) params.set('feedback', record.feedbackMode);
  params.set('locale', record.locale);
  if (player.hideHeader) params.set('hideHeader', '1');
  if (player.debug) params.set('debug', '1');
  // Where the jump is going while it goes, so a reload mid-way still goes there; then where it is.
  const index = player.jump.target ?? player.page?.index ?? null;
  if (index !== null && index > 0 && (player.page === null || player.page.count > 1)) params.set('page', String(index + 1));
  if (provider.id !== 'default') params.set('provider', provider.id);
  history.replaceState(null, '', `?${params.toString()}`);
}

// ---------------------------------------------------------------- the CET account

/**
 * Who is signed in, from the same provider the studio uses (`@cet/asset-sso`).
 *
 * Display only. Nothing here authorises anything: on a CET hostname the gateway in front of this
 * domain is what decides who may reach it, and off one there is no session to show.
 */
async function renderAccount(): Promise<void> {
  if (!ssoAvailable()) {
    ui.accountName.textContent = 'No CET session here';
    ui.accountRole.textContent = 'Sign-in needs a cet.ac.il hostname';
    ui.accountSession.hidden = true;
    return;
  }
  const who = await currentSession();
  ui.accountSession.hidden = false;
  if (who === null) {
    ui.accountName.textContent = 'Not signed in';
    ui.accountRole.textContent = 'CET account';
    ui.accountSession.textContent = 'Sign in';
    ui.accountSession.onclick = () => void signIn(record.locale === 'en' ? 'en' : 'he');
    return;
  }
  ui.accountName.textContent = who.displayName;
  ui.accountRole.textContent = who.role ?? 'role not declared';
  ui.accountSession.textContent = 'Sign out';
  ui.accountSession.onclick = () => void signOut();
  for (const avatar of [ui.accountInitials, ui.accountAvatar]) {
    avatar.textContent = initialsOf(who.displayName);
    avatar.dataset['named'] = 'true';
  }
  ui.account.setAttribute('aria-label', `${who.displayName} - account`);
}

function openAccount(open: boolean): void {
  ui.accountPop.hidden = !open;
  ui.account.setAttribute('aria-expanded', String(open));
}

ui.account.addEventListener('click', () => openAccount(ui.accountPop.hidden));
document.addEventListener('click', (event) => {
  const path = event.composedPath();
  if (!ui.accountPop.hidden && !path.includes(ui.accountPop) && !path.includes(ui.account)) openAccount(false);
});
document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && !ui.accountPop.hidden) {
    openAccount(false);
    ui.account.focus();
  }
});

// ---------------------------------------------------------------- wiring

function enterFullscreen(): void {
  void ui.stage.requestFullscreen?.().catch(() => trace('fullscreen refused by the browser'));
}

ui.traceToggle.addEventListener('click', () => {
  ui.trace.hidden = !ui.trace.hidden;
  document.body.classList.toggle('trace-open', !ui.trace.hidden);
  ui.traceToggle.setAttribute('aria-pressed', String(!ui.trace.hidden));
});
ui.traceClear.addEventListener('click', () => {
  ui.log.replaceChildren();
  player.events = 0;
  ui.traceCount.textContent = '0';
  ui.xapiLog.replaceChildren();
  xapi.count = 0;
  ui.xapiCount.textContent = '0';
});
ui.fullscreen.addEventListener('click', enterFullscreen);
const footer = createFooter(ui.controls, provider.footer, {
  // The page's Check asks the asset to check its question (D98); the default's checks the activity.
  check: () => (provider.hostCheck === true ? host.checkPage() : void host.check()),
  tool: (id) => host.pressTool(id),
  // The learner taking the controls ends any `?page=` jump still on its way.
  back: () => {
    // Off the summary page, back to the activity's last page, where the learner left it.
    if (player.onSummary) {
      player.onSummary = false;
      render();
      return;
    }
    endJump();
    host.navigate('back');
  },
  next: () => {
    endJump();
    // Past the activity's last page, onto the summary — once there is one.
    const last = player.page === null || player.page.index >= player.page.count - 1;
    if (summaryOffered() && record.submitted && last) {
      player.onSummary = true;
      render();
      return;
    }
    // The navigation bar's Next is the page's "continue" — the one thing that completes a page.
    completePage();
    host.navigate('next');
  },
  submit: () => void submitAsked(),
  reset: () => host.reset('the host'),
  // A submitted attempt was mounted read-only, and `canWrite` only reaches an asset at init (D50) —
  // so a new attempt is a reset AND a fresh mount, or the controls stay locked.
  again: () => {
    host.reset('the host');
    void mount();
  },
  finish: () => void finish(),
});

/** "התשובות שלי": back to the activity's first page, to review it — locked, with its marks (as LO). */
ui.summaryAnswers.addEventListener('click', () => {
  player.onSummary = false;
  for (let back = player.page?.index ?? 0; back > 0; back -= 1) host.navigate('back');
  render();
});

/**
 * The bars float over the page, so the page has to know how tall they are to keep content clear of
 * them — and both wrap at narrow widths, so the heights are measured rather than assumed.
 */
function trackBarHeights(): void {
  const root = document.documentElement.style;
  const measure = (): void => {
    root.setProperty('--top-h', `${ui.top.offsetHeight}px`);
    root.setProperty('--foot-h', `${ui.controls.hidden ? 0 : ui.controls.offsetHeight}px`);
  };
  const observer = new ResizeObserver(measure);
  observer.observe(ui.top);
  observer.observe(ui.controls);
  measure();
}

async function boot(): Promise<void> {
  await loadOrigins();
  const { id, version } = readUrl();
  document.body.classList.toggle('no-header', player.hideHeader);
  ui.traceToggle.hidden = !player.debug;
  trackBarHeights();
  trace(
    chosen.unknown === null
      ? `provider · ${provider.id}`
      : `provider "${chosen.unknown}" is not known · playing with ${provider.id}`,
  );
  // Not awaited: on a CET hostname it reaches the gateway, and the activity should not wait on it.
  void renderAccount();
  renderPicker();
  await choose(id, version);
}

void boot();
